import { Project } from '@beeboard/common';
import { plusXDaysString } from '@/utils/helpers/datetime';
import { isConnectionViolatedByDeadline } from '@/utils/helpers/violatedEdges';

type ProjectPhase = Project['phases'][number];

interface IntermediateForceDeadlineDates {
  minIdealDate?: string;
  minPermissibleDate?: string;
  maxIdealDate?: string;
  maxPermissibleDate?: string;
}

/**
 * The phase a picked deadline lands in (date-part compare) — the picker
 * counterpart of the map's phaseByPosition. A date on a phase's deadline
 * belongs to that phase (its final milestone's day). The forms put the phase
 * id into the update payload when it differs from the milestone's own phase.
 */
export function findPhaseForDeadline(
  project: Project,
  deadline: string,
): ProjectPhase | undefined {
  const day = deadline.split('T')[0];

  return project.phases.find((p) => day <= p.deadline.split('T')[0]);
}

// Picker window for an intermediate milestone's deadline, shared by the
// dues-edit dialog and the map's selected-milestone form (projects only).
// Mirrors the map drag (ioStore getIntermediateMilestone{Ideal,Permissible}*):
// - Permissible (hard) bounds: the surrounding done/unplanned phases — done
//   phases always block the move, unplanned phases block it unless the
//   milestone itself is unplanned. Planned/underway phases may be crossed;
//   the save carries the phase change (findPhaseForDeadline).
// - Ideal (no-warning) bounds: currently-valid connected activities only — a
//   date before the last connected activity or outside the own phase puts the
//   connections out of order (kept as violated, or removed on request), so
//   those ranges render as orange warning dates and are selectable
//   with force_update_deadline. Connections that are already violated do not
//   bound the window. Without (valid) connected activities the whole
//   permissible window is ideal. Sibling milestones never shrink the window —
//   crossing them is a normal move; landing exactly ON another milestone's
//   date is rejected by the shared validation on save (uniqueness rule).
export function calculateIntermediateMilestoneForceDeadlineDates(
  project: Project,
  milestoneId: string,
): IntermediateForceDeadlineDates {
  const phaseIndex = project.phases.findIndex((p) =>
    p.intermediate_milestones.some((m) => m.id === milestoneId),
  );
  const phase = project.phases[phaseIndex];
  if (!phase) {
    return {};
  }
  const milestone = phase.intermediate_milestones.find(
    (m) => m.id === milestoneId,
  );
  if (!milestone) {
    return {};
  }

  const blocksMove = (p: ProjectPhase) =>
    p.state === 'done' ||
    (p.state === 'unplanned' && milestone.state !== 'unplanned');

  const previousBlockingPhase = project.phases
    .slice(0, phaseIndex)
    .filter(blocksMove)
    .at(-1);
  const minPermissibleDate = plusXDaysString(
    previousBlockingPhase ? previousBlockingPhase.deadline : project.start_date,
    1,
  );

  // a blocking phase starts on the deadline of the phase before it; without
  // one the map end (last phase deadline) bounds the move
  const followingPhases = project.phases.slice(phaseIndex + 1);
  const nextBlockingIndex = followingPhases.findIndex(blocksMove);
  const nextBoundary =
    nextBlockingIndex === -1
      ? project.phases[project.phases.length - 1].deadline
      : nextBlockingIndex === 0
        ? phase.deadline
        : followingPhases[nextBlockingIndex - 1].deadline;
  const maxPermissibleDate = plusXDaysString(nextBoundary, -1);

  const lastConnectedActivity = project.activities
    .filter(
      (a) =>
        milestone.activities.includes(a.id) &&
        !isConnectionViolatedByDeadline(
          { phase: a.phase, deadline: a.current_deadline },
          { phase: phase.id, deadline: milestone.current_deadline },
        ),
    )
    .map((a) => a.current_deadline)
    .sort()
    .at(-1);

  const minIdealDate = lastConnectedActivity
    ? [
        minPermissibleDate,
        // at or after connected activities (backend rule, bypassed by force);
        // 0 days normalizes to UTC start-of-day ISO like the other candidates
        plusXDaysString(lastConnectedActivity, 0),
      ]
        .sort()
        .at(-1)
    : minPermissibleDate;
  const maxIdealDate = lastConnectedActivity
    ? plusXDaysString(phase.deadline, -1)
    : maxPermissibleDate;

  return {
    minIdealDate,
    minPermissibleDate,
    maxIdealDate,
    maxPermissibleDate,
  };
}
